/* global google */

/**
 * Abstraction class for Googel Maps API.
 * @see https://developers.google.com/maps/documentation/javascript/reference/map
 */
export default class GMap {
    constructor(mapEle) {
        this.map = null;
        this.mapEle = mapEle;
        this.mapBounds = null;
        this.markers = [];
        this.isLoaded = false;
        this.locatingUser = false;
        this.events = {
            loaded: [],
            changed: [],
        };
    }

    /**
     * Attach an event handler function to one of the custom events of GMap.
     *
     * @param {string} eventName Possible events are: loaded, changed.
     * @param {Function} callback The callback to execute when the event gets fired.
     */
    on(eventName, callback) {
        const self = this;

        if (eventName in self.events === false) {
            throw new Error(`Unknown event: ${eventName}.`);
        }

        self.events[eventName].push(callback);
    }

    /**
     * Listen for events
     * @see https://developers.google.com/maps/documentation/javascript/events
     */
    listenForEvents() {
        const self = this;

        // Event needed to states when the map is really loaded.
        const loadedListerner = self.map.addListener('tilesloaded', () => {
            // After it is loaded we don't need the event anymore.
            google.maps.event.removeListener(loadedListerner);

            self.isLoaded = true;

            self.events.loaded.forEach(callback => {
                callback();
            });
        });

        self.map.addListener('idle', () => {
            if (self.events.changed.length === 0) {
                return;
            }

            if (self.isLoaded === false) {
                return;
            }

            const currBounds = self.map.getBounds();
            const sameBounds = currBounds.equals(self.mapBounds);

            if (sameBounds === false) {
                self.mapBounds = currBounds;

                self.events.changed.forEach(callback => {
                    callback(self.mapBounds);
                });
            }
        });
    }

    /**
     * Builds the map.
     * @param {Object} settings JSON object that will be passed to "google.maps.Map" to customize the map.
     */
    buildMap(settings) {
        const self = this;

        // Initialize map
        // https://developers.google.com/maps/documentation/javascript/reference/
        self.map = new google.maps.Map(self.mapEle, {
            zoom: 1,
            center: new google.maps.LatLng(1, 1),
            disableDefaultUI: false,
            scrollwheel: false,
            ...settings,
        });

        self.listenForEvents();
    }

    /**
     * Sets the center of the map.
     * @param {google.maps.LatLn|google.maps.LatLngLiteral} latLng
     * @param {number} zoom
     */
    setCenter(latLng, zoom) {
        if (latLng !== null) {
            this.map.setCenter(latLng);
        }

        if (zoom !== null) {
            this.map.setZoom(zoom);
        }
    }

    /**
     * Locates the user.
     * @param {Function} cb Function to be executed when the geolocation finished.
     */
    locateUser(cb) {
        const self = this;

        if (self.locatingUser === true) {
            return;
        }

        // If the map is not fully loaded, then wait a bit.
        if (self.isLoaded === false) {
            setTimeout(() => {
                self.locateUser(cb);
            }, 500);
            return;
        }

        self.locatingUser = true;

        if ('geolocation' in navigator === false) {
            self.locatingUser = false;

            cb(new Error('Geolocation not available'));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            position => {
                const latLng = new google.maps.LatLng(
                    position.coords.latitude,
                    position.coords.longitude,
                );

                self.locatingUser = false;

                cb(null, latLng);
            },
            () => {
                self.locatingUser = false;

                cb(new Error('Error getting user position.'));
            },
        );
    }

    /**
     * Place the markers in the map.
     *
     * @param {Array} markers List of markers.
     * @param {Object} defSettings Default settings for markers. Each marker can overwrite the default settings.
     * @param {Function} cb Callback to parse each item individualy.
     */
    placeMarkers(markers, defSettings, cb) {
        const self = this;
        const bounds = new google.maps.LatLngBounds();
        const defSettingsParsed = defSettings || {};

        markers.forEach(item => {
            const parsedItem = cb(item);

            if (parsedItem === null) {
                return;
            }

            const markerProps = {
                // map: self.map,
                ...defSettingsParsed,
                ...parsedItem,
            };

            // https://developers.google.com/maps/documentation/javascript/markers
            const marker = new google.maps.Marker(markerProps);

            this.markers.push(marker);

            bounds.extend(markerProps.position);

            if ('infoWindowContent' in parsedItem) {
                // https://developers.google.com/maps/documentation/javascript/infowindows
                const infoWindow = new google.maps.InfoWindow({
                    content: parsedItem.infoWindowContent,
                });

                google.maps.event.addListener(marker, 'click', () => {
                    infoWindow.open({
                        anchor: marker,
                        map: google.maps,
                        shouldFocus: true,
                    });
                });
            } else if ('url' in parsedItem) {
                google.maps.event.addListener(marker, 'click', () => {
                    window.location.href = parsedItem.url;
                });
            }
        });

        // Show the map with all the markers until the user is located.
        self.map.fitBounds(bounds);
    }

    /**
     * Calculates the distance from the given coords.
     *
     * @param {google.maps.LatLng} coords LatLng to calculate from.
     */
    calcDistances(coords) {
        const self = this;

        self.markers.forEach((marker, idx) => {
            const locCoords = marker.position;

            /** @const {number} distance - Distance in meters. */
            const distance = google.maps.geometry.spherical.computeDistanceBetween(
                coords,
                locCoords,
            );

            self.markers[idx].cv_distance = distance;
        });
    }

    /**
     * Sort the markers by distance.
     *
     * @param {google.maps.LatLng} latLng The value used to sort the markers.
     */
    sortLocationsByDistance(latLng) {
        this.calcDistances(latLng);

        const keyName = `cv_distance`;

        return this.markers.sort((a, b) => {
            const x = a[keyName];
            const y = b[keyName];
            let returnVal = 0;

            if (x < y) {
                returnVal = -1;
            } else if (x > y) {
                returnVal = 1;
            }

            return returnVal;
        });
    }

    // /**
    //  * Builds locations in bounds.
    //  * @param {google.maps.LatLngBounds} bounds
    //  * @param {Function} cb Callback to be execute for each location.
    //  */
    // buildLocationsInBound(bounds, cb) {
    //     const self = this;
    //     const boundsToShow = new google.maps.LatLngBounds();

    //     self.sortLocations('distance', bounds.getCenter());

    //     self.markers.forEach((marker, idx) => {
    //         const num = idx + 1;
    //         const shouldBeVisible = bounds.contains(marker.position);

    //         cb(shouldBeVisible, num, marker.cv_id);

    //         // Setting the label for the marker in the map.
    //         marker.setLabel(num.toString());

    //         marker.setMap(shouldBeVisible ? self.map : null);

    //         if (shouldBeVisible) {
    //             boundsToShow.extend(marker.position);
    //         }
    //     });

    //     self.status = 'build-locations-in-bounds';

    //     if (boundsToShow.isEmpty()) {
    //         self.map.fitBounds(bounds);
    //     } else {
    //         self.map.fitBounds(boundsToShow);

    //         const maxZoom = 14;
    //         const actualZoom = self.map.getZoom();

    //         if (actualZoom > maxZoom) {
    //             self.map.setZoom(maxZoom);
    //         }
    //     }
    // }

    // /**
    //  * Builds locations by distance.
    //  * @param {google.maps.LatLng} latLng
    //  * @param {Function} cb Callback to be execute for each location. The callback should return boolean to state if the marker should be displayed or not in the map.
    //  */
    // buildLocationsByDistance(latLng, cb) {
    //     const self = this;
    //     const boundsToShow = new google.maps.LatLngBounds();

    //     // Sort locations by distance from the given coords.
    //     self.sortLocations('distance', latLng);

    //     // Reorder markers based on the last sorting.
    //     self.markers.forEach((marker, idx) => {
    //         const num = idx + 1;
    //         const shouldBeVisible = cb(num, marker.cv_id);

    //         // Setting the label for the marker in the map.
    //         marker.setLabel(num.toString());
    //         marker.setMap(shouldBeVisible ? self.map : null);

    //         if (shouldBeVisible) {
    //             boundsToShow.extend(marker.position);
    //         }
    //     });

    //     self.status = 'build-locations-by-distance';

    //     boundsToShow.extend(latLng);
    //     self.map.fitBounds(boundsToShow);
    // }

    // buildLocationsList() {
    //     const self = this;
    //     // reset numLocations to 0
    //     self.numLocations = 0;
    //     const geocoder = new google.maps.Geocoder();
    //     // Geocode the user input (get latitude and longitude) and save in an object
    //     geocoder.geocode(
    //         { address: self.searchLocation },
    //         (results, status) => {
    //             if (status === google.maps.GeocoderStatus.OK) {
    //                 self.searchCoords = new google.maps.LatLng(
    //                     results[0].geometry.location.lat(),
    //                     results[0].geometry.location.lng(),
    //                 );
    //                 self.getDistances(self.searchCoords);
    //                 // Loop the array of marker data to get location info
    //                 $.each(self.markers, (i, el) => {
    //                     // Check to see if the marker is within the visible bounds of the map
    //                     // If it is, increment numLocations (for text at the top of the screen - "## Locations")
    //                     // Then use the Mustache template to build the div for the store list and add it to the store list
    //                     if (self.mapBounds.contains(el.position)) {
    //                         self.numLocations++;
    //                         this.sortedArray.push(self.storeData[i]);
    //                     }
    //                 });
    //                 if (this.url) {
    //                     this.sortedOutArray = self.sortArray(
    //                         this.sortedArray,
    //                         'distance',
    //                     );
    //                 } else {
    //                     this.sortedOutArray = self.sortArray(
    //                         this.sortedArray,
    //                         'name',
    //                     );
    //                 }
    //                 this.$listContainer.empty();
    //                 self.renderClosest(this.sortedOutArray);
    //                 this.sortedOutArray = [];
    //                 this.sortedArray = [];
    //                 favoriteLocation.activateStar(favoriteLocation.faveStore);
    //                 // $('div.gmnoprint button').attr('aria-label', 'Zoom in').click();
    //                 if (this.url) {
    //                     $('span.distance').show();
    //                 } else {
    //                     $('span.distance').hide();
    //                 }
    //                 // Change the number at the top of the screen to show the number of visible stores
    //                 self.setLocationNumber();
    //             }
    //         },
    //     );
    // }

    // renderClosest(sortedOutArray) {
    //     const self = this;

    //     $.each(sortedOutArray, i => {
    //         const output = Mustache.render(
    //             self.storeTemplate,
    //             sortedOutArray[i],
    //         );

    //         this.$listContainer.append(output);
    //     });
    //     if (this.reset < 1) {
    //         $('div.gmnoprint button')
    //             .attr('aria-label', 'Zoom in')
    //             .click();
    //     }
    //     this.reset++;
    // }
}
